import { createContext, useState, useCallback, useContext, useEffect } from 'react'

export const VoiceContext = createContext(null)

/**
 * Voice Context Provider
 * Manages global mute/deafen state and provides methods to control voice settings
 */
export function VoiceProvider({ children }) {
  // Load initial state from localStorage
  const [isMuted, setIsMuted] = useState(() => {
    if (typeof window !== 'undefined') {
      return localStorage.getItem('gjyl-voice-muted') === 'true'
    }
    return false
  })

  const [isDeafened, setIsDeafened] = useState(() => {
    if (typeof window !== 'undefined') {
      return localStorage.getItem('gjyl-voice-deafened') === 'true'
    }
    return false
  })

  const [isPushToTalkActive, setIsPushToTalkActive] = useState(false)
  const [pushToTalkEnabled, setPushToTalkEnabled] = useState(false)

  // Persist state changes to localStorage
  useEffect(() => {
    localStorage.setItem('gjyl-voice-muted', isMuted.toString())
  }, [isMuted])

  useEffect(() => {
    localStorage.setItem('gjyl-voice-deafened', isDeafened.toString())
  }, [isDeafened])

  const toggleMute = useCallback(() => {
    setIsMuted(prev => {
      const newMuted = !prev
      // Dispatch event for any listeners (e.g., WebRTC handlers)
      window.dispatchEvent(new CustomEvent('voice-mute-change', { detail: newMuted }))
      return newMuted
    })
  }, [])

  const toggleDeafen = useCallback(() => {
    setIsDeafened(prev => {
      const newDeafened = !prev
      // When deafening, also mute
      if (newDeafened) {
        setIsMuted(true)
        window.dispatchEvent(new CustomEvent('voice-mute-change', { detail: true }))
      }
      window.dispatchEvent(new CustomEvent('voice-deafen-change', { detail: newDeafened }))
      return newDeafened
    })
  }, [])

  const setMuted = useCallback((muted) => {
    setIsMuted(muted)
    window.dispatchEvent(new CustomEvent('voice-mute-change', { detail: muted }))
  }, [])

  const setDeafened = useCallback((deafened) => {
    setIsDeafened(deafened)
    if (deafened) {
      setIsMuted(true)
      window.dispatchEvent(new CustomEvent('voice-mute-change', { detail: true }))
    }
    window.dispatchEvent(new CustomEvent('voice-deafen-change', { detail: deafened }))
  }, [])

  const startPushToTalk = useCallback(() => {
    if (pushToTalkEnabled) {
      setIsPushToTalkActive(true)
      // When PTT active, unmute temporarily
      window.dispatchEvent(new CustomEvent('voice-ptt-change', { detail: true }))
    }
  }, [pushToTalkEnabled])

  const stopPushToTalk = useCallback(() => {
    if (pushToTalkEnabled) {
      setIsPushToTalkActive(false)
      window.dispatchEvent(new CustomEvent('voice-ptt-change', { detail: false }))
    }
  }, [pushToTalkEnabled])

  const value = {
    isMuted,
    isDeafened,
    isPushToTalkActive,
    pushToTalkEnabled,
    toggleMute,
    toggleDeafen,
    setMuted,
    setDeafened,
    startPushToTalk,
    stopPushToTalk,
    setPushToTalkEnabled
  }

  return (
    <VoiceContext.Provider value={value}>
      {children}
    </VoiceContext.Provider>
  )
}

/**
 * useVoice Hook
 * Provides easy access to voice state and methods
 */
export function useVoice() {
  const context = useContext(VoiceContext)

  if (!context) {
    throw new Error('useVoice must be used within a VoiceProvider')
  }

  return context
}
